// src/onboarding/ui/ProgressView.jsx
//
// The non-form panels of the wizard: starting, running, stale, completed, unavailable, not available.
// Decision 7: "fase k di N" (4 since v196) for a known stage, the stage name alone for an unknown one. ⛔ No
// percentage (the stages are not equal in length) and no cost copy.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  stageStep, STAGE_TOTAL, canRunAgain, lastAttemptFailed, lastAttemptOf, connectedSiteOf,
} from '../onboardingModel.js';
import { FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';
import { HINT, NOTE, PRIMARY, SECONDARY, ACTIONS } from './formStyles.js';
import { formatDate } from './formatDate.js';
import { getWebsiteSite } from '../../context/contextApi.js';

const MANAGE_PATH = '/tenant-admin/manage';
const CHIP_LINK = {
  display: 'inline-flex', alignItems: 'center', padding: '1px 10px', borderRadius: RADII.pill,
  background: SEMANTIC.info.bg, color: SEMANTIC.info.fg, fontFamily: FONT_SANS, fontSize: '12px',
  fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap',
};

// The KB-indexing line is only true for a workspace that HAS a site (acceptance 2026-10-02): a documents-only
// workspace still reads the placeholder (`is_sentinel`), so nothing would be indexed. A failed read hides the line.
function useConnectedSite(tenantId, enabled) {
  const [site, setSite] = useState('');
  useEffect(() => {
    if (!enabled || !tenantId) return undefined;
    let alive = true;
    getWebsiteSite({ tenant: tenantId })
      .then((read) => { if (alive) setSite(connectedSiteOf(read)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [tenantId, enabled]);
  return site;
}

export default function ProgressView({ panel, status, tenantId, onRestart, onRunAgain }) {
  const { t, i18n } = useTranslation('onboarding');
  const language = i18n.language?.split('-')[0] === 'en' ? 'en' : 'it';
  const p = status?.personalization || {};
  const connectedSite = useConnectedSite(tenantId, panel === 'completed');

  if (panel === 'starting') {
    return (
      <div role="status">
        <h3 style={{ margin: '0 0 6px' }}>{t('progress.startingTitle')}</h3>
        <p style={HINT}>{t('progress.starting')}</p>
      </div>
    );
  }

  if (panel === 'progress') {
    const step = stageStep(p.status);
    const stage = t(`stages.${p.status}`, { defaultValue: t('stages.unknown') });
    return (
      <div role="status">
        <h3 style={{ margin: '0 0 6px' }}>{t('progress.runningTitle')}</h3>
        <p style={{ margin: '0 0 6px' }}>
          {step ? t('progress.stageStep', { step, total: STAGE_TOTAL, stage }) : t('progress.stage', { stage })}
        </p>
        {p.started_at && <p style={HINT}>{t('progress.startedAt', { date: formatDate(p.started_at, language) })}</p>}
        {/* Acceptance 2026-10-02 (point 6): say WHERE the status lives — the badge next to the Gestione
            Workspace title — with a chip-link to it. An admin of the active workspace always reaches it. */}
        <p style={HINT}>
          {t('progress.keepWorking')}{' '}
          <Link to={MANAGE_PATH} style={CHIP_LINK}>{t('progress.manageLink')}</Link>
        </p>
      </div>
    );
  }

  if (panel === 'stale') {
    return (
      <div>
        <h3 style={{ margin: '0 0 6px' }}>{t('progress.staleTitle')}</h3>
        <p style={HINT}>{t('progress.stale')}</p>
        <div style={ACTIONS}><button type="button" onClick={onRestart} style={PRIMARY(false)}>{t('progress.restart')}</button></div>
      </div>
    );
  }

  if (panel === 'completed') {
    const lastFailed = lastAttemptFailed(status);
    const lastStage = lastFailed ? lastAttemptOf(status)?.failed_stage : null;
    return (
      <div>
        <h3 style={{ margin: '0 0 6px' }}>{t('progress.completedTitle')}</h3>
        {p.completed_at && <p style={{ margin: '0 0 6px' }}>{t('progress.completedAt', { date: formatDate(p.completed_at, language) })}</p>}
        {lastFailed && (
          <div role="status" style={NOTE(SEMANTIC.attenzione)}>
            {lastStage
              ? t('progress.lastFailedStage', { stage: t(`stages.${lastStage}`, { defaultValue: t('stages.unknown') }) })
              : t('progress.lastFailed')}
          </div>
        )}
        <p style={HINT}>{t('progress.completedBody')}</p>
        {connectedSite && <p style={HINT}>{t('progress.kbAfterRun')}</p>}
        <div style={ACTIONS}>
          <Link to="/tenant-admin/brand-voice" style={SECONDARY}>{t('progress.openBrandVoice')}</Link>
          {/* ⛔ A SECOND run only where the plan allows one (canRunAgain); the POST stays the gate. */}
          {canRunAgain(status) && (
            <button type="button" onClick={onRunAgain} style={PRIMARY(false)}>
              {t(lastFailed ? 'progress.retry' : 'progress.runAgain')}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (panel === 'notAvailable') {
    return (
      <div role="note" style={NOTE(SEMANTIC.neutro)}>
        <strong style={{ display: 'block', marginBottom: '4px' }}>{t('page.notAvailableTitle')}</strong>
        {t('page.notAvailable')}
      </div>
    );
  }

  if (panel === 'unavailable') return <div role="status" style={NOTE(SEMANTIC.neutro)}>{t('page.unavailable')}</div>;

  return <p role="status" style={HINT}>{t('page.loading')}</p>;
}
